import { getListMedia } from '@form-builder/api/api'
import { useApi } from '@form-builder/context/ApiContext'
import {
  Media,
  MediaFilters,
  MediaStatus,
  PaginatedMedia
} from '@form-builder/types'
import { Divider, Flex, Input, Modal, ModalProps, Radio, Spin } from 'antd'
import { useCallback, useEffect, useState } from 'react'

import { FormMediaSelect } from './FormMediaSelect'
import { useStyles } from './styles'

type Props = {
  contentType?: string
  onOk: (data: Media) => void
} & Omit<ModalProps, 'onOk'>

const redioButtons = [
  { label: 'All', value: MediaStatus.ALL },
  { label: 'Published', value: MediaStatus.PUBLISHED },
  { label: 'Draft', value: MediaStatus.DRAFT }
]

const DEFAULT_PAGE_SIZE = 10
const DEFAULT_PAGE_NUMBER = 1

export const FormMediaSelectModal = ({
  onOk,
  contentType,
  ...props
}: Props) => {
  const [media, setMedia] = useState<PaginatedMedia>()

  const { styles } = useStyles()
  const { apiData } = useApi()

  const [selectedMedia, setSelectedMedia] = useState<Media>()
  const [loading, setLoading] = useState(false)

  const queryParams = {
    pageNumber: DEFAULT_PAGE_NUMBER,
    pageSize: DEFAULT_PAGE_SIZE,
    contentType
  }

  const [filters, setFilters] = useState<MediaFilters>(queryParams)

  const onChangeFilters = (newFilters: Partial<MediaFilters>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
      pageNumber: newFilters.pageNumber ?? DEFAULT_PAGE_NUMBER
    }))
  }

  const fetchMediaList = useCallback(async () => {
    try {
      setLoading(true)
      const { pageNumber, pageSize, contentType, status, ...rest } = filters

      const query = JSON.stringify({
        page: pageNumber,
        content_type: contentType,
        page_size: pageSize,
        status: status !== MediaStatus.ALL ? status : undefined,
        ...rest
      })

      const params = new URLSearchParams(JSON.parse(query))

      const response = await getListMedia(apiData, params)

      setMedia(response)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [filters, props?.open])

  useEffect(() => {
    fetchMediaList()
  }, [fetchMediaList])

  const paginationConfig = {
    current: filters.pageNumber,
    total: media?.count || 0,
    pageSize: filters.pageSize,
    onChange: (pageNumber: number, pageSize: number) =>
      onChangeFilters({ pageNumber, pageSize })
  }

  const onClickCard = (file: Media) => {
    setSelectedMedia(file)
  }

  const onSave = () => {
    if (selectedMedia) {
      onOk(selectedMedia)
    }
    setSelectedMedia(undefined)
    setFilters({ ...filters, pageNumber: DEFAULT_PAGE_NUMBER })
  }

  return (
    <Modal
      title="Select media file"
      width={1200}
      height={500}
      open
      okButtonProps={{
        disabled: !selectedMedia
      }}
      destroyOnClose={true}
      onOk={onSave}
      {...props}
    >
      <Spin spinning={loading} size="large">
        <Flex gap={10}>
          <Input.Search
            defaultValue={filters.search}
            className={styles.flex}
            placeholder="Search"
            allowClear
            onSearch={(value) => onChangeFilters({ search: value })}
          />

          <Radio.Group
            defaultValue={filters.status || MediaStatus.ALL}
            buttonStyle="solid"
            onChange={(e) => onChangeFilters({ status: e.target.value })}
          >
            {redioButtons.map((item) => (
              <Radio.Button key={item.value} value={item.value}>
                {item.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Flex>
        <Divider />
        <FormMediaSelect
          onClick={onClickCard}
          media={media?.results}
          selectedMedia={selectedMedia}
          pagination={paginationConfig}
        />
      </Spin>
    </Modal>
  )
}
